import { createReducer } from "typesafe-actions";

import { SKIPPED_RECORDS_ENTITY_TYPES } from "@app/scene/dashboard/components/cards/SkippedRecords/consts";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import EntityTypesSupportedInSkippedRecords from "@app/types/dashboard/cards/skippedRecords/EntityTypesSupportedInSkippedRecords";
import SkippedRecordsCardConfiguration from "@app/types/dashboard/cards/skippedRecords/SkippedRecordsCardConfiguration";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import { createEmptyFilterModel } from "@app/util/filters/utils";

import {
  applySkippedRecordsDrillDownListViewSettings,
  fetchSkippedRecordsCardDrillDownData,
  hideSkippedRecordsDrillDown,
  showSkippedRecordsDrillDown,
  SkippedRecordsCardActions,
} from "./actions";

export interface SkippedRecordsCardState {
  configuration?: SkippedRecordsCardConfiguration; // also works as a "visible" trigger, visible if set
  loading?: boolean;
  // Spans stepped back on the card face when View Data was opened. Held beside the
  // configuration rather than inside it, because navigation is deliberately not persisted.
  spansBack?: number;
  totalFilteredRecords: Record<EntityTypesSupportedInSkippedRecords, number>;
  totalRecords: Record<EntityTypesSupportedInSkippedRecords, number>;
  viewStates: Record<EntityTypesSupportedInSkippedRecords, DrillDownViewState>;
}

export const skippedRecordsInitialState: SkippedRecordsCardState = {
  totalFilteredRecords: SKIPPED_RECORDS_ENTITY_TYPES.reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInSkippedRecords, number>
  ),
  totalRecords: SKIPPED_RECORDS_ENTITY_TYPES.reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInSkippedRecords, number>
  ),
  viewStates: SKIPPED_RECORDS_ENTITY_TYPES.reduce(
    (result, entityType) => {
      result[entityType] = {
        columns: [],
        filter: createEmptyFilterModel(),
        range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
        sort: [],
      };
      return result;
    },
    {} as Record<EntityTypesSupportedInSkippedRecords, DrillDownViewState>
  ),
};

const skippedRecordsState = createReducer<SkippedRecordsCardState, SkippedRecordsCardActions>(
  skippedRecordsInitialState
)
  .handleAction(showSkippedRecordsDrillDown.request, (state, { payload }) => ({
    ...state,
    configuration: payload.configuration,
    spansBack: payload.spansBack,
  }))
  .handleAction(showSkippedRecordsDrillDown.success, (state, { payload }) => ({
    ...state,
    viewStates: payload.viewStates,
  }))
  .handleAction(hideSkippedRecordsDrillDown, (state) => ({
    ...state,
    configuration: undefined,
    spansBack: undefined,
  }))
  .handleAction(fetchSkippedRecordsCardDrillDownData.request, (state) => ({
    ...state,
    loading: true,
  }))
  .handleAction(fetchSkippedRecordsCardDrillDownData.success, (state, { payload }) => ({
    ...state,
    loading: false,
    totalFilteredRecords: {
      ...state.totalFilteredRecords,
      [payload.entityType]: payload.totalFilteredRecords,
    },
    totalRecords: {
      ...state.totalRecords,
      [payload.entityType]: payload.totalRecords,
    },
  }))
  .handleAction(fetchSkippedRecordsCardDrillDownData.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(applySkippedRecordsDrillDownListViewSettings, (state, { payload }) => {
    const viewState = state.viewStates[payload.entityType];
    return {
      ...state,
      viewStates: {
        ...state.viewStates,
        [payload.entityType]: {
          ...viewState,
          columns: payload.columns ?? viewState.columns,
          filter: payload.filter ?? viewState.filter,
          range: payload.range ?? viewState.range,
          sort: payload.sort ?? viewState.sort,
        },
      },
    };
  });

export default skippedRecordsState;
